import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { DateRange } from 'react-day-picker';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import {
  EventLifecycleStatus,
  EventTimelineEntry,
  EventUnionWithPermissions,
  isDividendTimelineEvent,
  isPendingLifecycleStatus,
  POOL_EVENT_TYPES,
  SortByDate,
  SortByType,
  sortByTypeMap,
} from '../../types/events.types';
import { useCompanyContextReady } from '../company/use-company-context-ready.hook';
import { useFormat } from '../use-format.hook';
import {
  PENDING_CREATE_TTL_MS,
  PENDING_EDIT_TTL_MS,
  PendingCreateEvent,
  PendingEditEvent,
  pendingEventsStore,
  PendingMutationKind,
  PendingMutationSettleOutcome,
} from './pending-events.store';

// Poll cadence while any event mutation is still in the EVENT_MUTATION queue. Tiered by the
// age of this client's youngest own pending mutation: a queued small mutation settles
// server-side in ~1s (busy spill), so the first seconds poll fast and the cadence decays for
// the long tail (big size-spilled events keep polling without hammering the list endpoint).
// Server pending rows with no local entry (another user's mutation, page reload) poll slow —
// their age is unknown and their settle is not this client's wait.
export const PENDING_POLL_FAST_MS = 250;
export const PENDING_POLL_MEDIUM_MS = 500;
export const PENDING_POLL_SLOW_MS = 1000;
export const PENDING_POLL_FAST_WINDOW_MS = 5_000;
export const PENDING_POLL_MEDIUM_WINDOW_MS = 15_000;

export const resolvePendingPollIntervalMs = ({
  hasServerPending,
  now,
  pendingSinceMs,
}: {
  hasServerPending: boolean;
  now: number;
  pendingSinceMs: number[];
}): false | number => {
  if (pendingSinceMs.length === 0) {
    return hasServerPending ? PENDING_POLL_SLOW_MS : false;
  }
  const youngestAgeMs = now - Math.max(...pendingSinceMs);
  if (youngestAgeMs < PENDING_POLL_FAST_WINDOW_MS) return PENDING_POLL_FAST_MS;
  if (youngestAgeMs < PENDING_POLL_MEDIUM_WINDOW_MS) return PENDING_POLL_MEDIUM_MS;
  return PENDING_POLL_SLOW_MS;
};

// Decides whether a tracked edit/delete has settled, and how, from the latest list fetch.
// `null` = still pending. Only fetches newer than the request acknowledgement count — before
// that the server may not have marked the row pending yet, so an active row proves nothing.
// Absence settles as APPLIED for both kinds: a delete removes the row, and an edit apply can
// recreate the event under a NEW event id (the PATCH response returns the replacement id), so
// the old id vanishing is the settle signal. Absence only counts when this hook instance has
// previously observed the event in its list (`wasObserved`) — an event merely outside the
// active filter/page must not read as applied.
export const resolvePendingMutationOutcome = ({
  dataUpdatedAt,
  kind,
  now,
  pending,
  serverEvent,
  wasObserved,
}: {
  dataUpdatedAt: number;
  kind: PendingMutationKind;
  now: number;
  pending: PendingEditEvent;
  serverEvent: EventUnionWithPermissions | undefined;
  wasObserved: boolean;
}): null | PendingMutationSettleOutcome => {
  if (now - pending.requestedAtMs >= PENDING_EDIT_TTL_MS) {
    return PendingMutationSettleOutcome.EXPIRED;
  }
  if (pending.enqueuedAtMs === undefined || dataUpdatedAt <= pending.enqueuedAtMs) return null;
  if (!serverEvent) {
    return wasObserved ? PendingMutationSettleOutcome.APPLIED : null;
  }
  if (isPendingLifecycleStatus(serverEvent.lifecycleStatus)) return null;
  // Only an explicit FAILED status reports failure. A delete whose row is still present and
  // active is NOT called failed — the list is server-cached, so a response built before the
  // apply can still show the pre-mutation row, and a wrong error toast is worse than waiting
  // for the real FAILED status (or the silent TTL expiry).
  if (serverEvent.lifecycleStatus === EventLifecycleStatus.FAILED) {
    return PendingMutationSettleOutcome.FAILED;
  }
  // Present and active settles only the in-place edit apply (grant notes/files-only, the
  // fundraising isOpen toggle) — every other edit lands as a recreate and is caught above.
  return kind === PendingMutationKind.EDIT ? PendingMutationSettleOutcome.APPLIED : null;
};

// Stable reference for the "no pending creates" case — a fresh [] from the zustand selector on
// every render breaks useSyncExternalStore snapshot caching (infinite re-render loop).
const EMPTY_PENDING_CREATES: PendingCreateEvent[] = [];
const EMPTY_PENDING_EDITS: PendingEditEvent[] = [];
const EMPTY_PENDING_DELETES: PendingEditEvent[] = [];

// Derived data (snapshots/graph, current valuation, issued shares, transactions) is invalidated
// by the mutation hooks at request time, but with the async EVENT_MUTATION pipeline the request
// returns before the consumer applies the mutation — that early refetch re-caches pre-mutation
// data. These keys are re-invalidated when the polled list shows a mutation has settled.
// Notifications are here because deleting an event cascades its vesting tasks → exercise
// requests → request notifications: the Activity Center badge is a session-long mounted query
// (25s staleTime, no focus refetch) that nothing else invalidates, so without this it keeps the
// pre-delete count while the Requests list already shows one fewer.
const EVENT_MUTATION_SETTLE_QUERY_KEYS: readonly QueryKey[] = [
  QueryKey.GET_EVENT_TRANSACTIONS,
  QueryKey.GET_EVENT_TRANSACTIONS_COUNT,
  QueryKey.GET_ISSUED_SHARES,
  QueryKey.GET_SNAPSHOTS,
  QueryKey.GET_SNAPSHOT,
  QueryKey.GET_CURRENT_VALUATION,
  QueryKey.GET_APPROVE_SHARE_PLANS,
  QueryKey.GET_SHARE_PLAN_VESTING_TASKS,
  // My Holdings' reverse vesting position is projected from the issuance rows, so an edited or
  // removed repurchase schedule has to re-read once the mutation applies.
  QueryKey.GET_STAKEHOLDER_REVERSE_VESTING,
  QueryKey.GET_VESTING_GRAPH,
  QueryKey.GET_NOTIFICATIONS,
  QueryKey.GET_UNREAD_NOTIFICATION_COUNT,
];

// The event DETAIL is reset, not invalidated. By the time an edit/delete settles the sheet has
// been closed for a while, so its `useEvent` observer is gone — invalidating only marks the
// cached entry stale and it lingers for the default 5 min gcTime holding the PRE-apply event.
// Reopening the row then paints that snapshot instantly (no skeleton), and swaps it for the real
// data a moment later when the background refetch lands. Dropping the data outright makes the
// next open start from its loading skeleton and render applied data only; a still-mounted
// observer (deep link) refetches immediately instead.
const EVENT_MUTATION_SETTLE_RESET_QUERY_KEYS: readonly QueryKey[] = [QueryKey.GET_EVENT];

export type EventsResponse = {
  events: EventTimelineEntry[];
  totalPages: number;
};
export const calculateDateRangeFromDays = (days: number): { from: Date; to: Date } => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - days);
  return { from, to };
};

export const calculateDateRangeFromMonths = (months: number): { from: Date; to: Date } => {
  const to = new Date();
  const from = new Date();
  from.setMonth(to.getMonth() - months);
  return { from, to };
};

export const calculateDateRangeFromYears = (years: number): { from: Date; to: Date } => {
  const to = new Date();
  const from = new Date();
  from.setFullYear(to.getFullYear() - years);
  return { from, to };
};

export const dateFilterVariant: Record<
  SortByDate,
  (props: { from: Date | undefined; to: Date | undefined }) => {
    from: Date | undefined;
    to: Date | undefined;
  }
> = {
  [SortByDate.ALL_TIME]: () => ({ from: undefined, to: undefined }),
  [SortByDate.CUSTOM_DATE]: ({ from, to }) => ({ from, to }),
  [SortByDate.LAST_30_DAYS]: () => calculateDateRangeFromDays(30),
  [SortByDate.LAST_60_DAYS]: () => calculateDateRangeFromDays(60),
  [SortByDate.LAST_QUARTER]: () => calculateDateRangeFromMonths(3),
  [SortByDate.LAST_YEAR]: () => calculateDateRangeFromYears(1),
};

export const useEvents = ({
  companyId,
  typesFilter = [],
  currentPage = 0,
  take = 100,
  customDateFilter,
  dateFilter = SortByDate.ALL_TIME,
  transactionView,
  enabled = true,
  includeDividends = false,
  includePendingCreates = false,
}: {
  companyId: string;
  currentPage?: number;
  customDateFilter?: DateRange;
  dateFilter?: SortByDate;
  enabled?: boolean;
  includeDividends?: boolean;
  includePendingCreates?: boolean;
  take?: number;
  transactionView?: boolean;
  typesFilter?: SortByType[];
}) => {
  const queryClient = useQueryClient();
  const { format } = useFormat();
  const isCompanyContextReady = useCompanyContextReady(companyId);
  const pendingCreates = pendingEventsStore(
    (state) => state.pendingByCompany[companyId] ?? EMPTY_PENDING_CREATES,
  );
  const pendingEdits = pendingEventsStore(
    (state) => state.pendingEditsByCompany[companyId] ?? EMPTY_PENDING_EDITS,
  );
  const pendingDeletes = pendingEventsStore(
    (state) => state.pendingDeletesByCompany[companyId] ?? EMPTY_PENDING_DELETES,
  );

  const dateRange = dateFilterVariant[dateFilter]({
    from: customDateFilter?.from,
    to: customDateFilter?.to,
  });

  const dateRangeTo = dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined;
  const dateRangeFrom = dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined;

  const skip = (currentPage - 1 <= 0 ? 0 : currentPage - 1) * take;
  const isEventsQueryEnabled = !!companyId && enabled && isCompanyContextReady;
  const { data, dataUpdatedAt, fetchNextPage, hasNextPage, isLoading, isPending, refetch } =
    useInfiniteQuery<EventsResponse>({
      queryKey: [
        QueryKey.GET_EVENTS,
        {
          to: dateRangeTo,
          from: dateRangeFrom,
          typesFilter,
          currentPage,
          companyId,
          take,
          skip,
          transactionView,
          includeDividends,
        },
      ],
      queryFn: ({ pageParam = 0 }) =>
        ApiService.get(
          { endpoint: BackendRoute.COMPANIES, routePath: [companyId, 'events'] },
          {
            queryParams: {
              take,
              skip: currentPage ? skip : pageParam,
              'start-date': dateRangeFrom,
              'end-date': dateRangeTo,
              'types-filter':
                typesFilter.length === sortByTypeMap.length
                  ? ''
                  : typesFilter
                      // The Pools tab / filter covers pool creations and formal size adjustments.
                      .flatMap<string>((type) =>
                        type === SortByType.NEW_POOL ? POOL_EVENT_TYPES : [type],
                      )
                      .join(','),
              ...(includeDividends ? { 'include-dividends': 'true' } : {}),
            },
          },
        ),

      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.totalPages > allPages.length ? allPages.length * take : undefined,
      enabled: isEventsQueryEnabled,
      // Poll while a queued create is unreconciled or any fetched event is pending edit/delete,
      // so the optimistic UI settles once the EVENT_MUTATION consumer applies the mutation.
      refetchInterval: (query) => {
        const hasServerPending = !!query.state.data?.pages.some((page) =>
          page.events.some(
            (entry) =>
              !isDividendTimelineEvent(entry) && isPendingLifecycleStatus(entry.lifecycleStatus),
          ),
        );
        return resolvePendingPollIntervalMs({
          hasServerPending,
          now: Date.now(),
          pendingSinceMs: [
            ...pendingCreates.map((pending) => pending.createdAtMs),
            ...pendingEdits.map((pending) => pending.requestedAtMs),
            ...pendingDeletes.map((pending) => pending.requestedAtMs),
          ],
        });
      },
    });

  const isPageLoading = !isEventsQueryEnabled || isLoading || (isPending && isEventsQueryEnabled);

  // Tracked edits/deletes that must keep their row in the pending skeleton. The optimistic
  // PENDING_* status written by the mutation hooks lives in the query cache, so the FIRST poll
  // that completes before the apply commits replaces it with the server's ACTIVE row — the
  // skeleton flashed off mid-mutation, the stale row reappeared, then the settled list jumped
  // in (an inline apply never marks the server row pending at all). The store is the source of
  // truth for "this client's mutation is still in flight", so overlay it onto every fetch: the
  // skeleton then holds from click until the settling fetch, where the store entry and the old
  // row leave together. A server-side FAILED wins over the overlay — the settle effect below
  // fires the failure outcome from the same fetch.
  const pendingStatusOverlay = useMemo(() => {
    const overlay = new Map<string, EventLifecycleStatus>();
    for (const pending of pendingEdits) {
      overlay.set(pending.eventId, EventLifecycleStatus.PENDING_EDIT);
    }
    for (const pending of pendingDeletes) {
      overlay.set(pending.eventId, EventLifecycleStatus.PENDING_DELETE);
    }
    return overlay;
  }, [pendingEdits, pendingDeletes]);

  const serverEntries = useMemo(
    () =>
      data?.pages?.reduce<EventTimelineEntry[]>((acc, page) => {
        return [...acc, ...page.events];
      }, []) ?? [],
    [data?.pages],
  );

  // Render-boundary only. The settle effects below MUST keep reading the raw server status —
  // overlaying before them would mask an in-place settle (grant notes-only, the isOpen toggle)
  // as still pending and deadlock the skeleton until the TTL expiry.
  const applyPendingStatusOverlay = useCallback(
    (entry: EventTimelineEntry): EventTimelineEntry => {
      if (isDividendTimelineEvent(entry)) return entry;
      const overlaidStatus = pendingStatusOverlay.get(entry.eventId);
      if (
        !overlaidStatus ||
        entry.lifecycleStatus === EventLifecycleStatus.FAILED ||
        isPendingLifecycleStatus(entry.lifecycleStatus)
      ) {
        return entry;
      }
      return { ...entry, lifecycleStatus: overlaidStatus };
    },
    [pendingStatusOverlay],
  );

  // Dividend payouts only arrive when `includeDividends` is set; `events` stays events-only so
  // existing consumers keep their shape, while `entries` carries the merged timeline.
  const serverEvents = useMemo(
    () =>
      serverEntries.filter(
        (entry): entry is EventUnionWithPermissions => !isDividendTimelineEvent(entry),
      ),
    [serverEntries],
  );

  // Reconcile on `eventId` (the real event id the backend reserves and returns), NOT `id` —
  // mapEventToUnion overrides `id` with the subtype id for grants/fundraising, so it would never
  // match the reserved id and the skeleton would linger until a hard reload.
  const serverEventIds = useMemo(
    () => new Set(serverEvents.map((event) => event.eventId)),
    [serverEvents],
  );

  // A placeholder is still shown until its real event lands (reservedId in the fetched list) or
  // it ages out (failed / fundraising-issuance append that merged into an existing round).
  const visiblePendingCreates = useMemo(
    () =>
      pendingCreates.filter(
        (pending) =>
          Date.now() - pending.createdAtMs < PENDING_CREATE_TTL_MS &&
          !(pending.reservedId && serverEventIds.has(pending.reservedId)),
      ),
    [pendingCreates, serverEventIds],
  );

  // The placeholder carries identity + type + lifecycleStatus only (see `buildPlaceholderEvent`),
  // so it is safe to render ONLY where a pending row short-circuits to a skeleton — the events
  // timeline. Every other consumer (comboboxes, cap table selects, incorporation lookup) reads
  // subtype fields off the union and would crash on the missing ones, so placeholders are opt-in.
  const renderedPendingCreates = includePendingCreates
    ? visiblePendingCreates
    : EMPTY_PENDING_CREATES;

  const eventsList = useMemo(
    () => [
      ...renderedPendingCreates.map((pending) => pending.event),
      ...serverEvents.map((event) => applyPendingStatusOverlay(event) as EventUnionWithPermissions),
    ],
    [renderedPendingCreates, serverEvents, applyPendingStatusOverlay],
  );

  const entriesList = useMemo<EventTimelineEntry[]>(
    () => [
      ...renderedPendingCreates.map((pending) => pending.event),
      ...serverEntries.map(applyPendingStatusOverlay),
    ],
    [renderedPendingCreates, serverEntries, applyPendingStatusOverlay],
  );

  const invalidateSettledMutationQueries = useCallback(() => {
    for (const key of EVENT_MUTATION_SETTLE_QUERY_KEYS) {
      queryClient.invalidateQueries({ queryKey: [key] });
    }
    for (const key of EVENT_MUTATION_SETTLE_RESET_QUERY_KEYS) {
      queryClient.resetQueries({ queryKey: [key] });
    }
  }, [queryClient]);

  // Settle reconciled or aged-out placeholders (runs on each poll-driven refetch). Either kind
  // of settle means the queued create finished server-side (the real event landed non-pending —
  // APPLIED or FAILED per its lifecycle status — or the TTL elapsed: a failed request, or an
  // append-merged fundraising/issuance create that folded into an existing round and never
  // exposes its reserved id, which settles EXPIRED = silent). The store fires the call-site
  // `onApplySettled` (create toast) exactly once; re-invalidate the derived queries here — the
  // request-time invalidation in useCreate ran before the EVENT_MUTATION consumer applied the
  // mutation. A landed row still wearing a pending status keeps waiting: the placeholder is
  // already hidden (reserved id is in the list), but the outcome is not yet known.
  useEffect(() => {
    const now = Date.now();
    const settlements = pendingCreates.flatMap((pending) => {
      if (now - pending.createdAtMs >= PENDING_CREATE_TTL_MS) {
        return [{ outcome: PendingMutationSettleOutcome.EXPIRED, tempId: pending.tempId }];
      }
      const landedEvent = pending.reservedId
        ? serverEvents.find((event) => event.eventId === pending.reservedId)
        : undefined;
      if (!landedEvent || isPendingLifecycleStatus(landedEvent.lifecycleStatus)) return [];
      const outcome =
        landedEvent.lifecycleStatus === EventLifecycleStatus.FAILED
          ? PendingMutationSettleOutcome.FAILED
          : PendingMutationSettleOutcome.APPLIED;
      return [{ outcome, tempId: pending.tempId }];
    });

    if (settlements.length > 0) {
      pendingEventsStore.getState().settlePendingCreates(companyId, settlements);
      invalidateSettledMutationQueries();
    }
  }, [companyId, pendingCreates, serverEvents, invalidateSettledMutationQueries]);

  // The prune above is data-driven, so it only re-runs when the store or the fetched list changes
  // identity. A placeholder whose reserved id can never land (a queued fundraising/issuance create
  // that merged into an existing round) leaves the list unchanged from then on — react-query hands
  // back the same reference on every poll — so nothing would ever re-evaluate the TTL and the
  // skeleton would stay until a reload. Expire it on a timer instead.
  useEffect(() => {
    if (pendingCreates.length === 0) return;

    const nextExpiryMs = Math.min(
      ...pendingCreates.map((pending) => pending.createdAtMs + PENDING_CREATE_TTL_MS),
    );
    const timeout = setTimeout(
      () => {
        const expiredSettlements = pendingEventsStore
          .getState()
          .pendingByCompany[companyId]?.filter(
            (pending) => Date.now() - pending.createdAtMs >= PENDING_CREATE_TTL_MS,
          )
          .map((pending) => ({
            outcome: PendingMutationSettleOutcome.EXPIRED,
            tempId: pending.tempId,
          }));

        if (expiredSettlements?.length) {
          pendingEventsStore.getState().settlePendingCreates(companyId, expiredSettlements);
          invalidateSettledMutationQueries();
        }
      },
      Math.max(0, nextExpiryMs - Date.now()),
    );

    return () => clearTimeout(timeout);
  }, [companyId, pendingCreates, invalidateSettledMutationQueries]);

  // Same settle-time re-invalidation for edits/deletes: a server event leaving a pending
  // lifecycle status (settled in place, or removed by an applied delete) means the mutation
  // committed after the request-time invalidation already refetched.
  const pendingServerEventIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const pendingIds = new Set(
      serverEvents
        .filter((event) => isPendingLifecycleStatus(event.lifecycleStatus))
        .map((event) => event.eventId),
    );
    const hasSettledEvent = [...pendingServerEventIdsRef.current].some(
      (eventId) => !pendingIds.has(eventId),
    );
    pendingServerEventIdsRef.current = pendingIds;

    if (hasSettledEvent) {
      invalidateSettledMutationQueries();
    }
  }, [serverEvents, invalidateSettledMutationQueries]);

  // Every event id this hook instance has seen in its fetched list. A tracked edit/delete
  // settles as APPLIED on absence only when a previously-observed event disappears — an
  // instance whose filter or page never contained the event cannot conclude anything from
  // its absence.
  const observedServerEventIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    observedServerEventIdsRef.current = new Set();
  }, [companyId]);
  useEffect(() => {
    serverEvents.forEach((event) => observedServerEventIdsRef.current.add(event.eventId));
  }, [serverEvents]);

  // Settle queued edits/deletes tracked in the pending store: a list fetch completed after the
  // request was acknowledged that shows the event no longer pending (edit) or gone (delete)
  // means the EVENT_MUTATION apply committed — re-invalidate the derived queries and let the
  // store fire the call-site `onApplySettled` (success/failure toast) exactly once. The settle
  // must be store-tracked because a fast apply can win the race with the first fetch, so a
  // pending lifecycle status is never observed. TTL stops a failed or never-observed mutation
  // from polling forever (EXPIRED settles silently).
  useEffect(() => {
    if (pendingEdits.length === 0 && pendingDeletes.length === 0) return;

    const now = Date.now();
    const toSettlements = (pendings: PendingEditEvent[], kind: PendingMutationKind) =>
      pendings.flatMap((pending) => {
        const outcome = resolvePendingMutationOutcome({
          dataUpdatedAt,
          kind,
          now,
          pending,
          serverEvent: serverEvents.find((event) => event.eventId === pending.eventId),
          wasObserved: observedServerEventIdsRef.current.has(pending.eventId),
        });
        return outcome ? [{ eventId: pending.eventId, outcome }] : [];
      });

    const editSettlements = toSettlements(pendingEdits, PendingMutationKind.EDIT);
    const deleteSettlements = toSettlements(pendingDeletes, PendingMutationKind.DELETE);

    if (editSettlements.length > 0) {
      pendingEventsStore.getState().settlePendingEdits(companyId, editSettlements);
    }
    if (deleteSettlements.length > 0) {
      pendingEventsStore.getState().settlePendingDeletes(companyId, deleteSettlements);
    }
    if (editSettlements.length + deleteSettlements.length > 0) {
      invalidateSettledMutationQueries();
    }
  }, [
    companyId,
    pendingEdits,
    pendingDeletes,
    serverEvents,
    dataUpdatedAt,
    invalidateSettledMutationQueries,
  ]);

  const invalidateQuery = () => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_EVENTS] });
  };

  return {
    invalidateQuery,
    events: eventsList || [],
    entries: entriesList,
    fetchNextPage,
    totalPages: data?.pages[0]?.totalPages || 0,
    hasNextPage,
    isLoading: isPageLoading,
    isPageLoading,
    isEmpty: eventsList?.length === 0,
    refetch,
    isSearchResultEmpty: eventsList?.length === 0 && dateFilter !== SortByDate.ALL_TIME,
  };
};
